import {
  useCallback,
  useLayoutEffect,
  useState,
  type ComponentPropsWithoutRef,
  type CSSProperties,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';

/** Keep this clear of every viewport edge. */
const GUTTER = 8;

type AnchoredPopProps = Omit<ComponentPropsWithoutRef<'div'>, 'style' | 'ref'> & {
  /** The control the popover hangs off — its rect is the anchor. */
  anchor: RefObject<HTMLElement | null>;
  /** The popover's own root. The CALLER owns it, because dismissal
   *  (`useDismissMenu`) is a containment test on exactly this node. */
  popRef: RefObject<HTMLDivElement>;
  /** Which of the anchor's edges the popover lines its matching edge up with.
   *  Both filmstrip surfaces are right-aligned; `left` is here for the next one. */
  align?: 'left' | 'right';
  /** Space between the anchor and the popover. */
  gap?: number;
  /**
   * Where the popover goes relative to its anchor.
   *
   * `below` (the default) is the menu/dropdown case: prefer down, flip up,
   * then clamp. `side` is the SUBMENU case — beside the row that opened it,
   * flipping to the other side when there is no room. A submenu placed below
   * would cover the menu it came from, which is the one thing a submenu must
   * not do.
   */
  placement?: 'below' | 'side';
};

/**
 * A popover that escapes its scroll container.
 *
 * The filmstrip is the reason this exists. `.filmstrip__list` sets
 * `overflow-y: auto`, and per spec a non-`visible` overflow on one axis forces
 * the other to compute to `auto` as well — so the strip clips on BOTH axes. An
 * absolutely-positioned popover inside a tile therefore had no way out: it was
 * cut off horizontally at the default 168px width (the menu's own floor is
 * 150px, and the strip is user-resizable down to 96px) and vertically on the
 * first tile, where opening upward — the old `useFlipUp`'s only remedy — put it
 * under the sticky section header and off the top of the scroller.
 *
 * So the popover is portalled to `document.body` and placed at `position: fixed`
 * against the viewport. Three rules it keeps:
 *
 * - **Prefer down, flip up, then clamp.** Flipping is decided against the
 *   VIEWPORT, which is what `useFlipUp` got wrong: it measured the viewport
 *   while the thing doing the clipping was the scroll container. When neither
 *   direction fits, the popover is clamped and given a `max-height` so it
 *   scrolls itself rather than running off the screen — that is what saves a
 *   long "Move to section" list on a short window.
 * - **Natural height is measured, never re-measured through the clamp.** Height
 *   comes from `scrollHeight` plus the border, not from `getBoundingClientRect`,
 *   so applying `max-height` cannot shrink what the next measurement sees. The
 *   obvious version — clear the style, measure, re-apply — feeds its own
 *   ResizeObserver and loops forever.
 * - **Scroll is listened for in the CAPTURE phase.** Scroll doesn't bubble to
 *   `window` from a nested scroller, and the filmstrip is a nested scroller, so
 *   a bubble-phase listener would never see the strip move under an open menu.
 *
 * Portalling does NOT break `useDismissMenu` — its check is
 * `ref.current.contains(target)`, a containment test against the node, not a
 * question about where the node sits in the tree. It does mean any host whose
 * dismissal ref is an ANCESTOR (SectionHeader passes its whole header row) has
 * to pass this popover's ref as well, or its own clicks read as outside clicks.
 */
export default function AnchoredPop({
  anchor,
  popRef,
  align = 'right',
  gap = 2,
  placement = 'below',
  children,
  ...rest
}: AnchoredPopProps) {
  const [pos, setPos] = useState<{ top: number; left: number; maxHeight: number } | null>(null);

  const place = useCallback(() => {
    const trigger = anchor.current;
    const pop = popRef.current;
    if (!trigger) return;
    if (!pop) return;

    const a = trigger.getBoundingClientRect();
    const width = pop.offsetWidth;
    // Content height, immune to whatever max-height the last placement applied.
    // offsetHeight - clientHeight is the border (there is no horizontal
    // scrollbar here), and both terms are clamped identically, so the
    // difference stays correct even while the popover is scrolling.
    const height = pop.scrollHeight + (pop.offsetHeight - pop.clientHeight);
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let left: number;
    let top: number;

    if (placement === 'side') {
      // Beside the anchor, preferring its right. Flips to the left when the
      // right would run off, which is what a submenu near the window edge has
      // to do; both are then clamped.
      left = a.right + gap;
      if (left + width > vw - GUTTER) left = a.left - gap - width;
      left = Math.min(Math.max(left, GUTTER), Math.max(GUTTER, vw - width - GUTTER));
      // Top-aligned with the row, then pulled up only as far as it must be to
      // fit — so the popover and the row it belongs to stay visually joined.
      top = a.top;
      if (top + height > vh - GUTTER) top = Math.max(GUTTER, vh - GUTTER - height);
    } else {
      left = align === 'right' ? a.right - width : a.left;
      left = Math.min(Math.max(left, GUTTER), Math.max(GUTTER, vw - width - GUTTER));

      top = a.bottom + gap;
      if (top + height > vh - GUTTER) {
        const above = a.top - gap - height;
        // Neither side fits: sit as low as the popover can and let max-height
        // below turn the overflow into a scroll.
        top = above >= GUTTER ? above : Math.max(GUTTER, vh - GUTTER - height);
      }
    }
    const maxHeight = vh - top - GUTTER;

    // Bail out on an unchanged placement, so the ResizeObserver below can't
    // ping-pong with the max-height this sets.
    setPos((prev) =>
      prev && prev.top === top && prev.left === left && prev.maxHeight === maxHeight ? prev : { top, left, maxHeight },
    );
  }, [anchor, popRef, align, gap, placement]);

  useLayoutEffect(() => {
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    // The popover changes height WHILE open — "Move to section" expands a
    // submenu under the item that opened it — so its own size is a placement
    // input, not just the window's.
    const observer = new ResizeObserver(place);
    const pop = popRef.current;
    if (pop) observer.observe(pop);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      observer.disconnect();
    };
  }, [place, popRef]);

  // Hidden for the first render only: the layout effect above places it before
  // the browser paints, so nothing is ever seen in the top-left corner.
  const style: CSSProperties = pos
    ? { top: pos.top, left: pos.left, maxHeight: pos.maxHeight }
    : { top: 0, left: 0, visibility: 'hidden' };

  return createPortal(
    <div ref={popRef} style={style} {...rest}>
      {children}
    </div>,
    document.body,
  );
}
